/**
 * Primary Premium Walk — the credit walk from the Schedule's SIR-
 * adjusted premium down to the Adjusted Premium that feeds Additional
 * Coverages.
 *
 * All four steps are present as of OR-1514 and all four are rates as of
 * OR-1540 (experience was briefly a dollar amount): base rate, quality,
 * experience, corridor, each applied as (1 − rate). Adjusted Premium stays
 * backend-owned — nothing here recomputes it.
 *
 * Row order follows the approved design (OR-1522): Quality sits above Base
 * Rate. The backend applies them base-rate-first, but both steps are
 * multiplicative so the order on screen changes nothing in the number.
 */
import { CurrencyDollarCircle } from "@untitledui/icons";
import { FormGroup } from "../../../../components/form-group";
import { MoneySelect } from "../../../../components/money-select";
import { PercentInput } from "../../../../components/percent-input";
import { SelectWithCustom } from "../../../../components/select-with-custom";
import type { PremiumSelectionDerived, PremiumSelectionInputs } from "../../../../domain/pricing";
import { CardRows, ComputedRow, FieldRow } from "../components/field-row";
import { useBlurFlash } from "../components/use-blur-flash";
import { QUALITY_CREDIT_PRESETS, SIR_PRESETS, clampMoneyToWire, displayMoneyValue } from "../premium-selection-types";
import { FIELD_TOOLTIPS } from "../tooltips";
import type { InputKey, PendingMap } from "../use-premium-selection";

interface PrimaryPremiumWalkCardProps {
    inputs: PremiumSelectionInputs;
    derived: PremiumSelectionDerived | null;
    pending: PendingMap;
    setInput: (key: InputKey, value: string) => void;
    setAndCommit: (key: InputKey, value: string) => void;
    commit: (key: InputKey) => void;
}

export function PrimaryPremiumWalkCard({ inputs, derived, pending, setInput, setAndCommit, commit }: PrimaryPremiumWalkCardProps) {
    const sirFlash = useBlurFlash();
    const baseRateFlash = useBlurFlash();
    const qualityFlash = useBlurFlash();
    const experienceFlash = useBlurFlash();
    const corridorFlash = useBlurFlash();

    return (
        <FormGroup
            title="Primary Premium Walk"
            icon={CurrencyDollarCircle}
            headerDivider={false}
            data-autotest="premium-selection-primary-walk-card"
        >
            <CardRows>
                <FieldRow label="Declared" flashKey={sirFlash.flashKey} data-autotest="premium-selection-primary-walk-declared-row">
                    <MoneySelect
                        aria-label="Declared"
                        value={inputs.sir}
                        presets={SIR_PRESETS}
                        onChange={(next) => {
                            // Keep the PUT inside the wire's Numeric(14,2) range.
                            setAndCommit("sir", clampMoneyToWire(next));
                            sirFlash.flash();
                        }}
                    />
                </FieldRow>

                <ComputedRow
                    label="Premium w/ Retro-date Credit"
                    tooltip={FIELD_TOOLTIPS.retroDateCreditPremium}
                    value={displayMoneyValue(derived?.retroDateCreditPremium ?? null)}
                    isPending={Boolean(pending.retroDateCreditPremium) || derived === null}
                    data-autotest="premium-selection-primary-walk-retro-date-credit-premium-row"
                />

                <ComputedRow
                    label="Premium w/ SIR Credit"
                    tooltip={FIELD_TOOLTIPS.premiumWithSirCredit}
                    value={displayMoneyValue(derived?.premiumWithSirCredit ?? null)}
                    isPending={Boolean(pending.premiumWithSirCredit) || derived === null}
                    data-autotest="premium-selection-primary-walk-premium-with-sir-credit-row"
                />

                <FieldRow
                    label="Quality Credit/Debit"
                    tooltip={FIELD_TOOLTIPS.qualityCreditDebit}
                    flashKey={qualityFlash.flashKey}
                    data-autotest="premium-selection-primary-walk-quality-credit-debit-row"
                >
                    <SelectWithCustom
                        aria-label="Quality Credit/Debit"
                        value={inputs.qualityCreditPct}
                        presets={QUALITY_CREDIT_PRESETS}
                        formatCustomLabel={(v) => (v.trim().endsWith("%") ? v : `${v}%`)}
                        onChange={(next) => {
                            // Custom entries may arrive as "25%" — store the
                            // canonical whole-percent number, clamped to the
                            // wire's [0,1] bound (the PUT 422s past 100%).
                            const cleaned = next.replace(/[^\d.]/g, "");
                            const n = Number(cleaned);
                            const clamped = cleaned !== "" && Number.isFinite(n) ? String(Math.min(100, n)) : cleaned;
                            setAndCommit("qualityCreditPct", clamped);
                            qualityFlash.flash();
                        }}
                    />
                </FieldRow>

                {/* FIRST walk step (OR-1514). A rate, so the same PercentInput +
                  * [0,100] clamp as Corridor below — the wire bounds it to [0,1]
                  * and 422s past that. The value a new quote shows here is the
                  * backend's own seeded 15%, not a FE default. */}
                <FieldRow
                    label="Base Rate Credit"
                    flashKey={baseRateFlash.flashKey}
                    controlClassName="w-24"
                    data-autotest="premium-selection-primary-walk-base-rate-credit-row"
                >
                    <PercentInput
                        aria-label="Base Rate Credit"
                        value={inputs.baseRateCreditPct}
                        onChange={(raw) => setInput("baseRateCreditPct", raw)}
                        onBlur={() => {
                            const n = Number(inputs.baseRateCreditPct.replace(/,/g, ""));
                            const clamped = Number.isFinite(n) ? Math.min(100, Math.max(0, n)) : null;
                            if (clamped !== null && String(clamped) !== inputs.baseRateCreditPct) {
                                setAndCommit("baseRateCreditPct", String(clamped));
                            } else {
                                commit("baseRateCreditPct");
                            }
                            baseRateFlash.flash();
                        }}
                    />
                </FieldRow>

                {/* THIRD walk step. A rate since OR-1540 (was a dollar amount that
                  * re-priced itself whenever the premium under it moved), so the
                  * same PercentInput + [0,100] clamp as its neighbours. The label
                  * keeps design's "Credit/Debit" wording even though the wire
                  * bounds it to >= 0, matching Quality Credit/Debit above. */}
                <FieldRow
                    label="Experience Credit/Debit"
                    flashKey={experienceFlash.flashKey}
                    controlClassName="w-24"
                    data-autotest="premium-selection-primary-walk-experience-credit-row"
                >
                    <PercentInput
                        aria-label="Experience Credit/Debit"
                        value={inputs.experienceCreditPct}
                        onChange={(raw) => setInput("experienceCreditPct", raw)}
                        onBlur={() => {
                            const n = Number(inputs.experienceCreditPct.replace(/,/g, ""));
                            const clamped = Number.isFinite(n) ? Math.min(100, Math.max(0, n)) : null;
                            if (clamped !== null && String(clamped) !== inputs.experienceCreditPct) {
                                setAndCommit("experienceCreditPct", String(clamped));
                            } else {
                                commit("experienceCreditPct");
                            }
                            experienceFlash.flash();
                        }}
                    />
                </FieldRow>

                {/* A RATE since OR-1315/OR-1319 (was a dollar credit) — edited
                  * as a whole percent, applied multiplicatively in the walk.
                  * The wire bounds it to [0,1] (the PUT 422s past 100%), so
                  * out-of-range typing clamps to the valid range on blur. */}
                <FieldRow
                    label="Corridor Credits"
                    flashKey={corridorFlash.flashKey}
                    controlClassName="w-24"
                    data-autotest="premium-selection-primary-walk-corridor-credits-row"
                >
                    <PercentInput
                        aria-label="Corridor Credits"
                        value={inputs.corridorCreditPct}
                        onChange={(raw) => setInput("corridorCreditPct", raw)}
                        onBlur={() => {
                            const n = Number(inputs.corridorCreditPct.replace(/,/g, ""));
                            const clamped = Number.isFinite(n) ? Math.min(100, Math.max(0, n)) : null;
                            if (clamped !== null && String(clamped) !== inputs.corridorCreditPct) {
                                setAndCommit("corridorCreditPct", String(clamped));
                            } else {
                                commit("corridorCreditPct");
                            }
                            corridorFlash.flash();
                        }}
                    />
                </FieldRow>

                <ComputedRow
                    label="Adjusted Premium"
                    tooltip={FIELD_TOOLTIPS.adjustedPremium}
                    value={displayMoneyValue(derived?.adjustedPremium ?? null)}
                    isPending={Boolean(pending.adjustedPremium) || derived === null}
                    highlighted
                    data-autotest="premium-selection-primary-walk-adjusted-premium-row"
                />
            </CardRows>
        </FormGroup>
    );
}
